import React, { useState } from 'react';
import { useTheme } from '@mui/material';
import useBundleTranslation from 'i18n';
import { BackendNode } from './types';
import { SAT_R, VIEW_W, LABEL_EDGE_PAD, getBgColor, wrapCenterLabel } from './layout';
import { fitLabel, labelWidth } from './fitLabel';
import { REPORT_ACTIVE_STROKE, REPORT_ACTIVE_STROKE_WIDTH, satelliteColor } from './nodeTypeColors';

const CENTER_MASK_PAD = 4;
const TOOLTIP_H = 28;
const ALERT_COLOR = '#e05c5c';
const SAT_LABEL_FONT = 11;
const SAT_LABEL_GAP = 7;
// Horizontal room the alert triangle takes up past the end of the label text
const SAT_ALERT_RESERVE = 18;

// A node hidden from Concierge fades to KG_FADE; when the selected report also uses it, the
// lighter KG_FADE_IN_REPORT keeps the report outline readable — and those are exactly the nodes
// the steward is here to act on (a pending report's measures are unavailable BECAUSE the report
// hasn't published).
const KG_FADE = 0.3;
const KG_FADE_IN_REPORT = 0.55;

export function nodeCircleOpacity(disabled: boolean, inReport: boolean, hovered: boolean): number {
    if (!disabled) return hovered ? 0.75 : 1;
    return (inReport ? KG_FADE_IN_REPORT : KG_FADE) + (hovered ? 0.2 : 0);
}

export function nodeLabelOpacity(disabled: boolean, inReport: boolean, hovered: boolean): number {
    if (!disabled) return 1;
    return (inReport ? KG_FADE_IN_REPORT : KG_FADE) + (hovered ? 0.3 : 0.1);
}

export function reportOutline(inReport: boolean) {
    return inReport ? { stroke: REPORT_ACTIVE_STROKE, strokeWidth: REPORT_ACTIVE_STROKE_WIDTH } : {};
}

// The report-membership payload and the graph payload both key on llm_learning_node.object_uid,
// so uid equality is the whole match.
export function isReportMember(node: BackendNode, memberUids?: ReadonlySet<string>): boolean {
    return !!node.object_uid && true === memberUids?.has(node.object_uid);
}

// Small upward triangle with its own tooltip
export function AlertTriangle({
    x,
    y,
    text,
    link,
    onHoverNode,
}: {
    x: number;
    y: number;
    text?: string;
    link?: string;
    onHoverNode?: (id: number | null) => void;
}) {
    const [hovered, setHovered] = useState(false);
    const size = 11;
    const points = `${x},${y - size} ${x - size * 0.6},${y} ${x + size * 0.6},${y}`;
    const tooltipW = text ? Math.max(140, text.length * 5.8 + 20) : 0;

    return (
        <g
            style={{ cursor: link ? 'pointer' : 'default' }}
            onMouseEnter={(e) => {
                e.stopPropagation();
                onHoverNode?.(null);
                setHovered(true);
            }}
            onMouseLeave={(e) => {
                e.stopPropagation();
                setHovered(false);
            }}
            onClick={
                link
                    ? (e) => {
                          e.stopPropagation();
                          window.open(link, '_self');
                      }
                    : undefined
            }
        >
            <polygon points={points} fill={ALERT_COLOR} />
            {hovered && text && (
                <g style={{ pointerEvents: 'none' }}>
                    <rect
                        x={x - tooltipW / 2}
                        y={y - size - TOOLTIP_H - 6}
                        width={tooltipW}
                        height={TOOLTIP_H}
                        rx={4}
                        fill="#1f2937"
                    />
                    <text
                        x={x}
                        y={y - size - TOOLTIP_H / 2 - 6}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={11}
                        fill="white"
                    >
                        {text}
                    </text>
                </g>
            )}
        </g>
    );
}

// Dark bubble above (cx, cy), kept inside the viewBox horizontally so a long name near an edge
// stays readable.
function TooltipBubble({ cx, cy, text }: { cx: number; cy: number; text: string }) {
    const w = Math.max(160, labelWidth(text, 11) + 24);
    const x = Math.min(Math.max(cx - w / 2, LABEL_EDGE_PAD), Math.max(VIEW_W - w - LABEL_EDGE_PAD, LABEL_EDGE_PAD));
    return (
        <g style={{ pointerEvents: 'none' }}>
            <rect x={x} y={cy - TOOLTIP_H - 8} width={w} height={TOOLTIP_H} rx={4} fill="#1f2937" />
            <text
                x={x + w / 2}
                y={cy - TOOLTIP_H / 2 - 8}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={11}
                fill="white"
            >
                {text}
            </text>
        </g>
    );
}

// Full node name for a label that had to be cut to fit its slot.
export function LabelTooltip({ cx, cy, name }: { cx: number; cy: number; name: string }) {
    return <TooltipBubble cx={cx} cy={cy} text={name} />;
}

export function DisabledTooltip({
    cx,
    cy,
    name,
    reasonText,
}: {
    cx: number;
    cy: number;
    name: string;
    reasonText?: string;
}) {
    const { t } = useBundleTranslation('components/common/knowledge-graph/panel');
    // §7: a disabled Date node with a typed exclusion reason shows that reason verbatim
    // instead of the generic "hidden from Concierge" message. Every other disabled type
    // keeps the generic message unchanged.
    const text = reasonText ?? t('hidden_from_concierge', { name });
    return <TooltipBubble cx={cx} cy={cy} text={text} />;
}

export function SatNode({
    node,
    cx,
    cy,
    labelSide,
    hovered,
    inReport = false,
    onHover,
    onClick,
}: {
    node: BackendNode;
    cx: number;
    cy: number;
    labelSide: 'left' | 'right';
    hovered: boolean;
    inReport?: boolean;
    onHover: (id: number | null) => void;
    onClick: (node: BackendNode) => void;
}) {
    const theme = useTheme();
    const color = satelliteColor(node.type, node.node_color);
    const disabled = node.enabled_in_concierge_ind === 'N';
    const circleOpacity = nodeCircleOpacity(disabled, inReport, hovered);
    const textOpacity = nodeLabelOpacity(disabled, inReport, hovered);
    const labelX = labelSide === 'left' ? cx - SAT_R - SAT_LABEL_GAP : cx + SAT_R + SAT_LABEL_GAP;
    const anchor = labelSide === 'left' ? 'end' : 'start';
    // A side label runs outward from its circle, so its room is the gap to the viewBox edge —
    // past that the browser clips it mid-word.
    const edgeDistance = labelSide === 'left' ? labelX : VIEW_W - labelX;
    const budget = edgeDistance - LABEL_EDGE_PAD - (node.alert ? SAT_ALERT_RESERVE : 0);
    const label = fitLabel(node.name, budget, SAT_LABEL_FONT);
    // Triangle sits to the right of label text regardless of label side
    const labelW = labelWidth(label.text, SAT_LABEL_FONT);
    const triangleX = labelSide === 'right' ? labelX + labelW + 2 : labelX - labelW - 2;

    return (
        <g
            data-testid="kg-node"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => onHover(node.llm_learning_node_id)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onClick(node)}
        >
            <circle cx={cx} cy={cy} r={SAT_R + 3} fill={getBgColor(theme)} />
            <circle cx={cx} cy={cy} r={SAT_R} fill={color} fillOpacity={circleOpacity} {...reportOutline(inReport)} />
            <text
                x={labelX}
                y={cy + 4}
                textAnchor={anchor}
                fontSize={SAT_LABEL_FONT}
                fill="#374151"
                fillOpacity={textOpacity}
            >
                {label.text}
            </text>
            {node.alert && (
                <AlertTriangle
                    x={triangleX}
                    y={cy + 4}
                    text={node.alert.text}
                    link={node.alert.link}
                    onHoverNode={onHover}
                />
            )}
            {disabled && hovered && (
                <DisabledTooltip
                    cx={cx}
                    cy={cy}
                    name={node.name}
                    reasonText={node.type === 'Date' ? (node.alert?.text ?? undefined) : undefined}
                />
            )}
            {!disabled && hovered && label.truncated && <LabelTooltip cx={cx} cy={cy} name={node.name} />}
        </g>
    );
}

export function CenterNode({
    node,
    cx,
    cy,
    r,
    hovered,
    disabled = false,
    inReport = false,
    color: colorOverride,
    onHover,
    onClick,
}: {
    node: BackendNode;
    cx: number;
    cy: number;
    r: number;
    hovered: boolean;
    disabled?: boolean;
    inReport?: boolean;
    // Host nodes (Model/Dataset/Domain) stay DB-driven via node.node_color (no shared legend
    // entry to key off). A satellite (Measure/Dimension/Date) rendered as the focused center node
    // must use its canonical satelliteColor() instead, same as every other rendering of that type —
    // callers pass this explicitly rather than CenterNode guessing from node.type itself.
    color?: string;
    onHover: (id: number | null) => void;
    onClick: (node: BackendNode) => void;
}) {
    const theme = useTheme();
    const lines = wrapCenterLabel(node.name);
    const color = colorOverride ?? node.node_color ?? '#CE1F6C';
    const fontSize = lines.length >= 3 ? 10 : 11;
    const lineH = fontSize + 3;
    const totalH = (lines.length - 1) * lineH;
    const circleOpacity = nodeCircleOpacity(disabled, inReport, hovered);

    return (
        <g
            data-testid="kg-node"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => onHover(node.llm_learning_node_id)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onClick(node)}
        >
            <circle cx={cx} cy={cy} r={r + CENTER_MASK_PAD} fill={getBgColor(theme)} />
            <circle cx={cx} cy={cy} r={r} fill={color} fillOpacity={circleOpacity} {...reportOutline(inReport)} />
            {lines.map((line, i) => (
                <text
                    key={i}
                    x={cx}
                    y={cy - totalH / 2 + i * lineH}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={fontSize}
                    fontWeight={600}
                    fill="white"
                    fillOpacity={disabled ? 0.6 : 1}
                    style={{ pointerEvents: 'none' }}
                >
                    {line}
                </text>
            ))}
            {disabled && hovered && (
                <DisabledTooltip
                    cx={cx}
                    cy={cy - r}
                    name={node.name}
                    reasonText={node.type === 'Date' ? (node.alert?.text ?? undefined) : undefined}
                />
            )}
        </g>
    );
}
